import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { MdAdd, MdEdit, MdDelete, MdLabel } from "react-icons/md";
import { createApi } from "../../../../core/api";

export const ReservationRequestTypeCrud = () => {
  const api = useMemo(() => createApi(), []);
  const { setPageMenus } = useOutletContext<any>();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);

  const [keyValue, setKeyValue] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.reservationRequestType
      .getAll()
      .then((r) => {
        if (r?.ok) {
          const list = r.data?.data ?? r.data ?? [];
          setData(Array.isArray(list) ? list : []);
        }
      })
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    setPageMenus([
      {
        label: "Reservation",
        items: [
          {
            label: "Reservation Request Types",
            route: "/dashboard/reservation-request-type",
          },
        ],
      },
    ]);
    load();
    return () => setPageMenus([]);
  }, [load, setPageMenus]);

  const openCreate = () => {
    setEditing(null);
    setKeyValue("");
    setValue("");
    setDescription("");
    setError(null);
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditing(row);
    setKeyValue(row.key ?? "");
    setValue(row.value ?? "");
    setDescription(row.description ?? "");
    setError(null);
    setOpen(true);
  };

  const submit = async () => {
    if (!keyValue.trim() || !value.trim() || saving) return;

    setSaving(true);
    setError(null);

    try {
      const payload = {
        key: keyValue,
        value,
        description,
      };

      if (editing) {
        const r = await api.reservationRequestType.update(editing.id, payload);
        if (!r?.ok) throw new Error(r?.message || "Update failed");
      } else {
        const r = await api.reservationRequestType.create(payload);
        if (!r?.ok) throw new Error(r?.message || "Create failed");
      }

      setOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || "Unexpected error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setDeleteError(null);
  };

  const doDelete = async () => {
    if (!deleteId || deleting) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      const r = await api.reservationRequestType.delete(deleteId);
      if (!r?.ok) throw new Error(r?.message || "Delete failed");
      setDeleteId(null);
      load();
    } catch (e: any) {
      setDeleteError(e?.message || "Unexpected error occurred");
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<any[]>(
    () => [
      {
        key: "id",
        title: "ID",
        minWidth: 80,
        accessor: (r: any) => r.id,
      },
      {
        key: "key",
        title: "Key",
        minWidth: 200,
        accessor: (r: any) => r.key,
        render: (_: any, v: string) => (
          <Badge
            icon={MdLabel}
            label={v}
            color="#2563EB"
            bg="rgba(37,99,235,.15)"
          />
        ),
      },
      {
        key: "value",
        title: "Value",
        minWidth: 240,
        accessor: (r: any) => r.value,
      },
      {
        key: "description",
        title: "Description",
        minWidth: 300,
        accessor: (r: any) => r.description ?? "-",
      },
      {
        key: "createdDate",
        title: "Created",
        minWidth: 180,
        accessor: (r: any) =>
          r.createdDate ? new Date(r.createdDate).toLocaleString() : "-",
      },
      {
        key: "actions",
        title: "Actions",
        minWidth: 220,
        render: (row: any) => (
          <GroupButton
            items={[
              {
                id: "edit",
                icon: MdEdit,
                color: "#2563EB",
                onClick: () => openEdit(row),
              },
              {
                id: "delete",
                icon: MdDelete,
                color: "#DC2626",
                onClick: () => confirmDelete(row.id),
              },
            ]}
          />
        ),
      },
    ],
    []
  );

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Reservation Request Types</div>
          <div style={S.subTitle}>Manage reservation request types</div>
        </div>

        <Button variant="primary" icon={MdAdd} onClick={openCreate}>
          Create Type
        </Button>
      </div>

      <Table
        tableKey="reservation-request-type"
        loading={loading}
        columns={columns}
        data={data}
        features={{
          sortable: true,
          filterable: true,
          pagination: true,
          persistence: true,
        }}
      />

      <Modal open={open} onClose={() => (!saving ? setOpen(false) : null)}>
        <div style={S.modalTitle}>
          {editing ? "Update Request Type" : "Create Request Type"}
        </div>

        <div style={S.form}>
          <label style={S.label}>Key</label>
          <input
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
            style={S.input}
            disabled={saving}
          />

          <label style={S.label}>Value</label>
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            style={S.input}
            disabled={saving}
          />

          <label style={S.label}>Description</label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={S.input}
            disabled={saving}
          />

          {error && <div style={S.error}>{error}</div>}
        </div>

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={saving}
          >
            Save
          </Button>
        </div>
      </Modal>

      <Modal
        open={!!deleteId}
        onClose={() => (!deleting ? setDeleteId(null) : null)}
      >
        <div style={S.modalTitle}>Delete Request Type</div>

        <div style={{ fontSize: 14, color: "#334155" }}>
          Are you sure you want to delete this request type?
        </div>

        {deleteError && <div style={S.error}>{deleteError}</div>}

        <div style={S.actions}>
          <Button
            variant="ghost"
            onClick={() => setDeleteId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={doDelete}
            loading={deleting}
            disabled={deleting}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  wrap: {
    position: "absolute",
    inset: 0,
    padding: 20,
    background: "#F8FAFC",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0F172A",
  },
  subTitle: {
    fontSize: 13,
    color: "#64748B",
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 14,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: "#334155",
  },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 12px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
  error: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 600,
    color: "#DC2626",
  },
};
